// src/pages/DocumentDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Download, Share2, ExternalLink, FileText, Calendar, MapPin, User, AlertCircle, ArrowRight } from 'lucide-react';
import { apiService, admtApiService } from '../services/api';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../contexts/ToastContext';

const DocumentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ADMT archive documents live under /admt/document/:id and are served
  // from a different table via admtApiService instead of apiService.
  const isAdmt = location.pathname.startsWith('/admt');
  const source = isAdmt ? admtApiService : apiService;

  // Get return URL from state or default to reports
  const getBackUrl = () => {
    if (location.state?.returnUrl) {
      return location.state.returnUrl;
    }
    return isAdmt ? '/admt' : '/dashboard/reports';
  };

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const data = await source.getDocumentDetails(id);
        showToast('Document loaded successfully', 'success');

        setDoc(data);
      } catch (error) {
        console.error('Error fetching document:', error);
        setError('Failed to load document details');
        showToast('Failed to load document details', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDocument();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, showToast, isAdmt]);

  const handleDownload = async () => {
    try {
      showToast('Preparing download...', 'info');
      const blob = await source.downloadDocument(id);

      if (typeof window !== 'undefined' && doc) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${doc.report_type || 'document'}_${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        showToast('Download started', 'success');
      } else {
        showToast('Download is only supported in browser', 'error');
      }
    } catch (error) {
      console.error('Download error:', error);
      showToast('Download failed', 'error');
    }
  };

  const handleShare = () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: 'Sandvik Document',
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      showToast('Link copied to clipboard', 'success');
    }
  };

  const handleRelatedNavigation = (filterType, filterValue) => {
    if (!filterValue) {
      showToast('No data available for this filter', 'warning');
      return;
    }

    // Build URL params for the filter
    const params = new URLSearchParams();
    params.set(filterType, filterValue);

    // Navigate to reports page with the filter applied
    navigate(`${isAdmt ? '/admt' : '/dashboard/reports'}?${params.toString()}`);
  };

  if (loading) return <LoadingScreen />;

  if (error || !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-n5 mb-4" />
          <h1 className="text-xl text-gray-900 mb-2">Document not found</h1>
          <p className="text-gray-600 mb-4">The document you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate(getBackUrl())}
            className="bg-electric-blue text-white px-4 py-2 hover:bg-electric-blue-eb3"
          >
            Back to reports
          </button>
        </div>
      </div>
    );
  }

  const formatValue = (value) => {
    if (!value || value === null || value === undefined || value === '(Empty/Not Specified)') return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Enhanced related reports data with descriptions
  const getRelatedReportsData = () => {
    const relatedReports = [];

    if (doc.customer_name && formatValue(doc.customer_name) !== 'N/A') {
      relatedReports.push({
        type: 'customer_name',
        value: doc.customer_name,
        title: 'Reports by customer',
        description: `View all reports and site visits for ${doc.customer_name}`,
        subtitle: `Customer: ${doc.customer_name}`,
        icon: User,
        color: 'bg-blue-600 group-hover:bg-blue-700',
        textColor: 'text-blue-600'
      });
    }

    if (doc.site_name && formatValue(doc.site_name) !== 'N/A') {
      relatedReports.push({
        type: 'site_name',
        value: doc.site_name,
        title: 'Reports by site',
        description: `View all reports and activities at ${doc.site_name} location`,
        subtitle: `Site: ${doc.site_name}`,
        icon: MapPin,
        color: 'bg-green-600 group-hover:bg-green-700',
        textColor: 'text-green-600'
      });
    }

    if (doc.part_number && formatValue(doc.part_number) !== 'N/A') {
      relatedReports.push({
        type: 'part_number',
        value: doc.part_number,
        title: 'Reports by Part number',
        description: `View performance data and failure analysis for part ${doc.part_number}`,
        subtitle: `Part: ${doc.part_number}`,
        icon: (props) => (
          <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        ),
        color: 'bg-purple-600 group-hover:bg-purple-700',
        textColor: 'text-purple-600'
      });
    }

    if (doc.serial_number && formatValue(doc.serial_number) !== 'N/A') {
      relatedReports.push({
        type: 'serial_number',
        value: doc.serial_number,
        title: 'Reports by serial number',
        description: `Track the complete history and performance of serial ${doc.serial_number}`,
        subtitle: `Serial: ${doc.serial_number}`,
        icon: (props) => (
          <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
          </svg>
        ),
        color: 'bg-indigo-600 group-hover:bg-indigo-700',
        textColor: 'text-indigo-600'
      });
    }

    if (doc.rig_model && formatValue(doc.rig_model) !== 'N/A') {
      relatedReports.push({
        type: 'rig_model',
        value: doc.rig_model,
        title: 'Reports by Rig model',
        description: `Compare performance across all ${doc.rig_model} rigs and identify trends`,
        subtitle: `Rig model: ${doc.rig_model}`,
        icon: (props) => (
          <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        ),
        color: 'bg-electric-blue group-hover:bg-electric-blue-eb3',
        textColor: 'text-electric-blue'
      });
    }

    if (doc.country_name && formatValue(doc.country_name) !== 'N/A') {
      relatedReports.push({
        type: 'country_name',
        value: doc.country_name,
        title: 'Reports by country',
        description: `Analyze regional performance and trends across ${doc.country_name}`,
        subtitle: `Country: ${doc.country_name}`,
        icon: (props) => (
          <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        color: 'bg-teal-600 group-hover:bg-teal-700',
        textColor: 'text-teal-600'
      });
    }

    if (doc.failure_reason && formatValue(doc.failure_reason) !== 'N/A') {
      relatedReports.push({
        type: 'failure_reason',
        value: doc.failure_reason,
        title: 'Reports by failure type',
        description: `Investigate similar failure patterns and root causes related to "${doc.failure_reason}"`,
        subtitle: `Failure: ${doc.failure_reason}`,
        icon: AlertCircle,
        color: 'bg-red-600 group-hover:bg-red-700',
        textColor: 'text-red-600'
      });
    }

    return relatedReports;
  };
const hasValidValue = (value) => {
  return value && 
         value !== null && 
         value !== undefined && 
         value !== '(Empty/Not Specified)' && 
         typeof value === 'string' && 
         value.trim() !== '' && 
         value.toLowerCase() !== 'null' &&
         formatValue(value) !== 'N/A';
};
  const relatedReportsData = getRelatedReportsData();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-6 px-4">
        {/* Header with actions */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate(getBackUrl())}
            className="flex items-center space-x-2 text-gray-600 hover:text-black transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back to reports</span>
          </button>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="flex items-center space-x-2 bg-electric-blue text-white px-4 py-2 hover:bg-electric-blue-eb3"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </button>
            <button
              onClick={handleShare}
              className="flex items-center space-x-2 border border-gray-300 text-gray-700 px-4 py-2 hover:bg-gray-50"
            >
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </button>
          </div>
        </div>

        {/* Document Title */}
        <div className="mb-8">
          <h1 className="text-3xl text-gray-900 mb-2 text-left">
            {doc.report_type || 'Document Details'}
          </h1>
          <p className="text-gray-600 text-left">
            Customer name: {doc.customer_name} • {doc.date_of_visit}
          </p>
        </div>

        {/* Content Cards - Single Column Full Width */}
        <div className="space-y-8">
          
          {/* Basic Information */}
          <div className="bg-white shadow p-8">
            <h2 className="text-xl text-gray-900 mb-6 flex items-center text-left">
              <FileText className="h-5 w-5 mr-2 text-black" />
              Basic information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Report type</label>
                <p className="text-lg text-gray-900">{formatValue(doc.report_type)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Date of visit</label>
                <p className="text-lg text-gray-900">{doc.date_of_visit}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Customer name</label>
                <p className="text-lg text-gray-900">{formatValue(doc.customer_name)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Customer account</label>
                <p className="text-lg text-gray-900">{formatValue(doc.customer_entity)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Country</label>
                <p className="text-lg text-gray-900">{formatValue(doc.country_name)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Site</label>
                <p className="text-lg text-gray-900">{formatValue(doc.site_name)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Sales area</label>
                <p className="text-lg text-gray-900">{formatValue(doc.sales_area)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Entity</label>
                <p className="text-lg text-gray-900">{formatValue(doc.entity)}</p>
              </div>
            </div>
          </div>

          {/* Product Information */}
          <div className="bg-white shadow p-8">
            <h2 className="text-xl text-gray-900 mb-6 text-left">Product & equipment information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Part number</label>
                <p className="text-lg text-gray-900">{formatValue(doc.part_number)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Serial number</label>
                <p className="text-lg text-gray-900">{formatValue(doc.serial_number)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Rig model</label>
                <p className="text-lg text-gray-900">{formatValue(doc.rig_model)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Rig ID</label>
                <p className="text-lg text-gray-900">{formatValue(doc.rig_id)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Failure reason</label>
                <p className="text-lg text-gray-900">{formatValue(doc.failure_reason)}</p>
              </div>
              <div className="text-left">
                <label className="block text-sm text-gray-500 mb-2">Description</label>
                <p className="text-lg text-gray-900">{formatValue(doc.description)}</p>
              </div>
            </div>
          </div>

{/* Related Reports - Simple & Clean */}
<div className="bg-white shadow p-8">
  <h2 className="text-xl text-gray-900 mb-6 flex items-center text-left">
    <FileText className="h-5 w-5 mr-2 text-black" />
    Find related reports
  </h2>
  
  <div className="flex flex-wrap gap-3">
    {hasValidValue(doc.customer_name) && (
      <button
        onClick={() => handleRelatedNavigation('customer_name', doc.customer_name)}
        className="inline-flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 text-sm text-gray-700 hover:text-black transition-colors"
      >
        Other reports from {doc.customer_name}
        <ArrowRight className="h-3 w-3 ml-2" />
      </button>
    )}
    
    {hasValidValue(doc.site_name) && (
      <button
        onClick={() => handleRelatedNavigation('site_name', doc.site_name)}
        className="inline-flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 text-sm text-gray-700 hover:text-black transition-colors"
      >
        All activities at {doc.site_name}
        <ArrowRight className="h-3 w-3 ml-2" />
      </button>
    )}
    
    {hasValidValue(doc.part_number) && (
      <button
        onClick={() => handleRelatedNavigation('part_number', doc.part_number)}
        className="inline-flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 text-sm text-gray-700 hover:text-black transition-colors"
      >
        Performance data for part {doc.part_number}
        <ArrowRight className="h-3 w-3 ml-2" />
      </button>
    )}

    {hasValidValue(doc.serial_number) && (
      <button
        onClick={() => handleRelatedNavigation('serial_number', doc.serial_number)}
        className="inline-flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 text-sm text-gray-700 hover:text-black transition-colors"
      >
        History of serial {doc.serial_number}
        <ArrowRight className="h-3 w-3 ml-2" />
      </button>
    )}

    {hasValidValue(doc.rig_model) && (
      <button
        onClick={() => handleRelatedNavigation('rig_model', doc.rig_model)}
        className="inline-flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 text-sm text-gray-700 hover:text-black transition-colors"
      >
        All {doc.rig_model} rig reports
        <ArrowRight className="h-3 w-3 ml-2" />
      </button>
    )}

    {hasValidValue(doc.country_name) && (
      <button
        onClick={() => handleRelatedNavigation('country_name', doc.country_name)}
        className="inline-flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 text-sm text-gray-700 hover:text-black transition-colors"
      >
        Regional trends in {doc.country_name}
        <ArrowRight className="h-3 w-3 ml-2" />
      </button>
    )}

    {hasValidValue(doc.failure_reason) && (
      <button
        onClick={() => handleRelatedNavigation('failure_reason', doc.failure_reason)}
        className="inline-flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 text-sm text-gray-700 hover:text-black transition-colors"
      >
        Similar failure patterns: {doc.failure_reason}
        <ArrowRight className="h-3 w-3 ml-2" />
      </button>
    )}
  </div>

  {/* Show message if no related reports are available */}
  {![doc.customer_name, doc.site_name, doc.part_number, doc.serial_number, doc.rig_model, doc.country_name, doc.failure_reason].some(hasValidValue) && (
    <p className="text-gray-500 italic">No related report filters available for this document.</p>
  )}
</div>

        </div>
      </div>
    </div>
  );
};

export default DocumentDetailPage;
